// Centralised helpers + constants for the onboarding/payment flow.
//
// The onboarding migration replaces the in-app payment modal with a
// dedicated /onboarding page. This module keeps every reference to that
// route (and the rules around it) in one place so guards, redirects and
// nav helpers can stay in sync.
//
// Feature-flag (Vite env): VITE_ONBOARDING_PAGE = "1" enables the new
// page + guard. Any other value (including unset) leaves the old payment
// modal flow in place — making this a zero-risk additive change until
// every payment surface has been migrated.

export const ONBOARDING_PATH = "/onboarding";

/** Truthy when the user has settled (paid or used a promo code). */
export const PAID_STATUSES = ["paid", "promo"];

/**
 * Returns true if the onboarding page + guard should be active.
 *
 * Migration is complete — the new /onboarding flow is now the only
 * supported path. Kept as a function (rather than removing every call
 * site) so any future kill-switch can be reintroduced in one place.
 */
export const isOnboardingPageEnabled = () => true;

/**
 * Decide whether a user's payment status counts as "settled".
 * Strict: only the explicit "paid" / "promo" values pass. Missing,
 * empty, "unpaid", or any unknown value keeps the user gated. The
 * legacy backfill has run, so every existing user now has an explicit
 * paidStatus on their document.
 */
export const isPaid = (paidStatus) => PAID_STATUSES.includes(paidStatus);

/**
 * Routes that an unpaid signed-in user is still allowed to visit.
 * Anything not on this list (and not public) will bounce to /onboarding.
 *
 * Public routes (LandingPage, /sign-in, etc.) are already outside the
 * <IsAuthenticated> guard and therefore outside <RequirePayment> too;
 * they don't need to be listed here.
 */
export const PAYMENT_ALLOWLIST = [
  ONBOARDING_PATH,
  "/payment-sucess",
  "/user-info",
  "/twofact-auth",
];

/** True when `pathname` is in the unpaid allow-list. */
export const isPaymentAllowlisted = (pathname) => {
  if (!pathname) return false;
  // Exact + prefix-with-slash match so /onboarding/foo also passes.
  return PAYMENT_ALLOWLIST.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
};

/**
 * Imperative redirect to onboarding. Pass an optional `navigate` from
 * react-router (`useNavigate()`); falls back to a full-page assign so
 * this is usable from non-component code (e.g. axios interceptors).
 */
export const goToOnboarding = (navigate, { replace = true } = {}) => {
  if (typeof navigate === "function") {
    navigate(ONBOARDING_PATH, { replace });
  } else if (typeof window !== "undefined") {
    window.location.assign(ONBOARDING_PATH);
  }
};

/**
 * Build a query string from chain / payment context so /onboarding (or
 * the legacy "/" landing-with-modal) can pick up where the user left off.
 * Returns "" when none of the args are provided.
 */
export const buildPaymentContextQuery = ({
  paymentId = null,
  chainId = null,
  nodesId = null,
  intent = null,
  returnTo = null,
} = {}) => {
  const params = new URLSearchParams();
  if (paymentId && nodesId) {
    // Legacy callers pass chainId in the paymentId slot — mirror that.
    params.set("chainId", paymentId);
    params.set("nodesId", nodesId);
  } else if (paymentId) {
    params.set("paymentId", paymentId);
  } else if (chainId) {
    params.set("chainId", chainId);
    if (nodesId) params.set("nodesId", nodesId);
  }
  if (intent) params.set("intent", intent);
  if (returnTo) params.set("returnTo", returnTo);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const isSafeInternalReturnTo = (value) => {
  if (!value || typeof value !== "string") return false;
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  return !/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(value);
};

/**
 * Single source of truth for "where to send the user after a successful
 * sign-in / sign-up". When the onboarding page is enabled AND the user
 * is unpaid, route them to /onboarding (carrying chain/payment context
 * so the page can wire it into the checkout). Otherwise fall back to
 * the existing landing-page behaviour the callers used to inline.
 */
export const postAuthRedirect = (
  navigate,
  {
    paidStatus,
    paymentId = null,
    chainId = null,
    nodesId = null,
    intent = null,
    returnTo = null,
  } = {},
) => {
  const hasOnboardingIntent = intent === "get-started" || intent === "join-campaign";
  const ctx = buildPaymentContextQuery({
    paymentId,
    chainId,
    nodesId,
    intent,
    returnTo,
  });
  if (isSafeInternalReturnTo(returnTo)) {
    if (typeof navigate === "function") {
      navigate(returnTo, { replace: true });
      return;
    }
    if (typeof window !== "undefined") {
      window.location.assign(returnTo);
    }
    return;
  }
  if (isOnboardingPageEnabled() && (hasOnboardingIntent || !isPaid(paidStatus))) {
    if (typeof navigate === "function") {
      navigate(`${ONBOARDING_PATH}${ctx}`, { replace: true });
      return;
    }
    if (typeof window !== "undefined") {
      window.location.assign(`${ONBOARDING_PATH}${ctx}`);
    }
    return;
  }
  // Paid users (or flag off) keep the legacy landing-page route.
  const target = ctx ? `/${ctx}` : "/";
  if (typeof navigate === "function") {
    navigate(target);
  } else if (typeof window !== "undefined") {
    window.location.assign(target);
  }
};
